import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getTemplateCount } from '../../utils/services/documentAPI';
import { getallFolder } from '../../utils/services/folderAPI';
import { TagSearch } from './TagSearch';
import CreateResource from './CreateResource';
import { AppStoreContext } from '../../context/appStoreContext';
import styled from 'styled-components';
import { Popover, Icon, message } from 'antd';
import {
  FileTextIconSVG,
  PanelRightCloseIcon,
  PanelLeftCloseIcon,
  TagIcon,
  FolderIconSVG,
  UsersIcon,
  GlobeIcon,
  ClockIconSVG,
  CircleXIcon,
  ChevronRightIcon,
  ChevronDownIconSVG,
} from '../../images/svgIcons';
import { NEW_TEMPLATE_SECTION } from './translationValues';

const SidebarContainer = styled.div`
  border-right: 1px solid rgba(229, 231, 235, 0.8);
  height: 100%;
  width: ${props => (props.collapsed ? '0px' : '288px')};
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: ${props => (props.collapsed ? 'none' : 'blur(40px)')};
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
`;

const SidebarContent = styled.div`
  height: 100%;
  width: 288px;
  display: flex;
  flex-direction: column;
  opacity: ${props => (props.collapsed ? 0 : 1)};
  transition: opacity 0.2s ease;
  pointer-events: ${props => (props.collapsed ? 'none' : 'auto')};
`;

const ToggleButton = styled.div`
  position: absolute;
  top: 28px;
  right: ${props => (props.collapsed ? 'auto' : '24px')};
  left: ${props => (props.collapsed ? '24px' : 'auto')};
  border-radius: 8px;
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: ${props => (props.collapsed ? 'white' : 'transparent')};
  box-shadow: ${props => (props.collapsed ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none')};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
  &:hover {
    background: ${props => (props.collapsed ? '#f9fafb' : '#f3f4f6')};
    transform: scale(1.05);
  }
  svg {
    color: ${props => (props.collapsed ? '#4f46e5' : '#9ca3af')};
    transition: color 0.3s ease;
  }
`;

const LogoSection = styled.div`
  border-bottom: 1px solid rgba(229, 231, 235, 0.8);
  padding: 24px;
`;

const LogoContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LogoGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoIcon = styled.div`
  border-radius: 16px;
  height: 40px;
  width: 40px;
  background: linear-gradient(to bottom right, #4f46e5, #6d28d9);
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  svg {
    color: white;
  }
`;

const LogoTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const LogoSubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ViewSwitcherSection = styled.div`
  border-bottom: 1px solid rgba(229, 231, 235, 0.8);
  padding: 16px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  margin-bottom: 12px;
  width: 100%;
`;

const StyledSearchInput = styled.input`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  outline: none;
  width: 100%;
  padding: 8px 32px 8px 12px;
  font-size: 14px;
  transition: all 0.2s;
  &:focus {
    border-color: #a855f7;
    box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.1);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 0;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: color 0.2s;
  &:hover {
    color: #4b5563;
  }
`;

const ViewGrid = styled.div`
  border-radius: 12px;
  padding: 4px;
  background: #f3f4f6;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
`;

const ViewButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  background: ${props => (props.active ? 'white' : 'transparent')};
  color: ${props => (props.active ? '#111827' : '#4b5563')};
  box-shadow: ${props => (props.active ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none')};
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  transition: all 0.2s;
  ${props =>
    !props.disabled &&
    `&:hover {
    color: #111827;
  }`};
`;

const NavigationSection = styled.div`
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background: #d1d5db;
  }
`;

const NavGroup = styled.div`
  margin-bottom: 24px;
`;

const NavGroupHeader = styled.div`
  margin-bottom: 12px;
  padding: 0 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavGroupTitle = styled.h3`
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6b7280;
`;

const AddButton = styled.button`
  border: none;
  border-radius: 8px;
  padding: 4px;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #f3f4f6;
  }
  svg {
    color: #9ca3af;
  }
`;

const NavItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FolderButton = styled.button`
  border: none;
  border-radius: 18px;
  width: 100%;
  padding: 9px 12px;
  padding-left: ${props => (props.level ? `${12 + props.level * 16}px` : '12px')};
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => {
    if (props.selected) {
      if (props.status === 'all') return '#000';
      if (props.status === 'waiting') return 'linear-gradient(45deg, #f59e0b, #ea580c)';
      if (props.status === 'rejected') return 'linear-gradient(45deg, #ef4444, #f43f5e)';
      return 'linear-gradient(45deg, #6366f1, #7c3aed)';
    }
    return 'transparent';
  }};
  color: ${props => (props.selected ? 'white' : '#374151')};
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props =>
    props.selected && props.status === undefined
      ? '0 20px 15px -3px rgba(199, 210, 254, 0.6), 0 10px 6px -4px rgba(199, 210, 254, 0.6)'
      : 'none'};
  &:hover {
    background: ${props => (props.selected ? '' : '#f3f4f6')};
    transform: ${props => (props.selected ? '' : 'translateX(2px)')};
  }
  ${props =>
    props.selected &&
    `
    svg {
      color: #fff;
    }
  `};
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${props => (props.selected ? 'white' : '#9ca3af')};
  transition: transform 0.2s;
`;

const FolderIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${props => {
    if (props.selected) return 'white';
    if (props.status === 'waiting') return '#f59e0b';
    if (props.status === 'rejected') return '#ef4444';
    return '#6b7280';
  }};
  transition: color 0.2s;
`;

const FolderName = styled.span`
  flex: 1;
  font-size: 14px;
  text-align: left;
`;

const FolderCount = styled.span`
  border-radius: 9999px;
  padding: 2px 8px;
  font-size: 12px;
  background: ${props => {
    if (props.selected) return 'rgba(255, 255, 255, 0.2)';
    if (props.status === 'waiting') return '#fef3c7';
    if (props.status === 'rejected') return '#fee2e2';
    return '#e5e7eb';
  }};
  color: ${props => {
    if (props.selected) return 'white';
    if (props.status === 'waiting') return '#d97706';
    if (props.status === 'rejected') return '#dc2626';
    return '#4b5563';
  }};
  transition: all 0.2s;
`;

const SubFolders = styled.div`
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SidebarLoader = styled.div`
  height: 100px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 28px;
`;

const Empty = styled.div`
  padding-block: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  p {
    font-size: 13px;
  }
`;

const EmptyIcon = styled.div`
  margin-bottom: 5px;
  border-radius: 50%;
  height: 50px;
  width: 50px;
  background: #e5e7eb;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    color: #9ca3af;
  }
`;

function RecursiveFolderTree({ folders, selectedFolder, onSelectFolder, level = 0 }) {
  return (
    <>
      {folders?.map(folder => (
        <FolderItem
          key={folder.id}
          id={folder.id}
          name={folder.name}
          icon={<FolderIconSVG />}
          count={folder.count || folder.elements?.length}
          children={folder.subFolder}
          selectedFolder={selectedFolder}
          onSelectFolder={onSelectFolder}
          level={level}
          isExpandable={folder.subFolder?.length > 0}
        />
      ))}
    </>
  );
}

function FolderItem({
  id,
  name,
  icon,
  count = null,
  children,
  selectedFolder,
  onSelectFolder,
  level = 0,
  isExpandable = false,
  clearAllFilters,
  status,
  qaStatus,
  setQaStatus,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isSelected = selectedFolder.split('+')[0] === id.toString();

  const isQaStatus = status === 'waiting' || status === 'rejected';

  const handleFolderClick = () => {
    if (id === 'all') {
      clearAllFilters();
      return;
    }
    if (isQaStatus) {
      setQaStatus(prev => (prev === status ? null : status));
      return;
    }
    onSelectFolder(`${id}+${name}`);
    if (isExpandable) {
      setIsExpanded(prev => !prev);
    }
  };

  return (
    <div>
      <FolderButton
        onClick={handleFolderClick}
        level={level}
        selected={isQaStatus ? qaStatus === status : isSelected}
        status={status}
      >
        {isExpandable && (
          <ChevronIcon selected={isSelected}>
            {isExpanded ? <ChevronDownIconSVG /> : <ChevronRightIcon />}
          </ChevronIcon>
        )}
        <FolderIcon selected={isSelected} status={status}>
          {icon}
        </FolderIcon>
        <FolderName>{name}</FolderName>
        {count !== null && (
          <FolderCount selected={isSelected} status={status}>
            {count}
          </FolderCount>
        )}
      </FolderButton>

      {children && isExpanded && (
        <SubFolders>
          <RecursiveFolderTree
            folders={children}
            selectedFolder={selectedFolder}
            onSelectFolder={onSelectFolder}
            level={level + 1}
          />
        </SubFolders>
      )}
    </div>
  );
}

const keys = {
  categories: 'category',
  folders: 'folder',
  teams: 'resourceGroup',
  languages: 'language',
};

const Sidebar = ({
  organizationView,
  setOrganizationView,
  selectedView,
  collapsed,
  onToggleCollapse,
  setSearchQuery,
  selectedTags,
  setSelectedTags,
  selectedFolder,
  onSelectFolder,
  qaStatus,
  setQaStatus,
  clearAllFilters,
  allLanguages,
  searchInputRef,
}) => {
  const { t } = useTranslation(['NEW_TEMPLATE_SECTION']);
  const [isLoading, setIsLoading] = useState(false);
  const [templateCount, setTemplatesCount] = useState(null);

  const { checkUserAccessRoles } = useContext(AppStoreContext);

  const fetchTemplateCount = async () => {
    const resourceType =
      selectedView === 'documents'
        ? 'document'
        : selectedView === 'reusable_pages'
          ? 'flow'
          : 'block';

    setIsLoading(true);
    let response;
    try {
      if (organizationView === 'folders') {
        response = await getallFolder(false, false, true, null, resourceType);
      } else {
        response = await getTemplateCount(resourceType, keys[organizationView]);
      }
      if ([3221, 3222, 3223].includes(response?.statusCode) && response?.data) {
        setTemplatesCount(response.data);
      } else if (response?.statusCode === 607 && response?.data) {
        setTemplatesCount({ ...templateCount, folder: response.data });
      } else {
        message.error(
          t(
            NEW_TEMPLATE_SECTION.FAILED_TO_LOAD_TEMPLATE_COUNT.key,
            NEW_TEMPLATE_SECTION.FAILED_TO_LOAD_TEMPLATE_COUNT.value,
          ),
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const prevSelectedView = useRef('documents');
  useEffect(() => {
    if (prevSelectedView.current !== selectedView) {
      if (organizationView !== 'categories') {
        setOrganizationView('categories');
      } else {
        fetchTemplateCount();
      }
      setTemplatesCount(null);
      prevSelectedView.current = selectedView;
    } else {
      fetchTemplateCount();
    }
  }, [selectedView, organizationView]);

  const [showClear, setShowClear] = useState(false);

  const handleSearchInputChange = e => {
    if (e.key === 'Enter') {
      setSearchQuery(e.target.value);
    }
  };

  const onInputChange = e => {
    setShowClear(e.target.value.length > 0);
  };

  const handleClearSearch = () => {
    if (searchInputRef?.current) {
      searchInputRef.current.value = '';
      setSearchQuery('');
      setShowClear(false);
      searchInputRef.current.focus();
    }
  };

  const titleMap = {
    categories: t(
      NEW_TEMPLATE_SECTION.ALL_CATEGORIES.key,
      NEW_TEMPLATE_SECTION.ALL_CATEGORIES.value,
    ),
    folders: t(NEW_TEMPLATE_SECTION.ALL_FOLDERS.key, NEW_TEMPLATE_SECTION.ALL_FOLDERS.value),
    teams: t(NEW_TEMPLATE_SECTION.ALL_TEAMS.key, NEW_TEMPLATE_SECTION.ALL_TEAMS.value),
    languages: t(NEW_TEMPLATE_SECTION.ALL_LANGUAGES.key, NEW_TEMPLATE_SECTION.ALL_LANGUAGES.value),
  };

  const languageMap = Object.fromEntries(allLanguages?.map(l => [l.code, l.name]) ?? []);

  const languages =
    templateCount?.language
      ?.map(a => ({
        id: a.name,
        name: languageMap[a.name] || a.name,
        count: a.count,
      }))
      ?.filter(b => b.id != null) ?? [];

  return (
    <SidebarContainer collapsed={collapsed}>
      <ToggleButton collapsed={collapsed} onClick={onToggleCollapse}>
        {collapsed ? <PanelRightCloseIcon /> : <PanelLeftCloseIcon />}
      </ToggleButton>

      <SidebarContent collapsed={collapsed}>
        {/* Logo */}
        <LogoSection>
          <LogoContent>
            <LogoGroup>
              <LogoIcon>
                <FileTextIconSVG />
              </LogoIcon>
              <div>
                <LogoTitle>
                  {t(NEW_TEMPLATE_SECTION.SEARCH.key, NEW_TEMPLATE_SECTION.SEARCH.value)}
                </LogoTitle>
                <LogoSubtitle>
                  {t(NEW_TEMPLATE_SECTION.STUDIO.key, NEW_TEMPLATE_SECTION.STUDIO.value)}
                </LogoSubtitle>
              </div>
            </LogoGroup>
          </LogoContent>
        </LogoSection>

        {/* View Switcher */}
        <ViewSwitcherSection>
          {/* Search by name */}
          <SearchInputWrapper>
            <StyledSearchInput
              ref={searchInputRef}
              placeholder={t(
                NEW_TEMPLATE_SECTION.SEARCH_BY_NAME_PLACEHOLDER.key,
                NEW_TEMPLATE_SECTION.SEARCH_BY_NAME_PLACEHOLDER.value,
              )}
              onKeyDown={handleSearchInputChange}
              onChange={onInputChange}
            />
            {showClear && (
              <ClearButton onClick={handleClearSearch}>
                <CircleXIcon size={16} />
              </ClearButton>
            )}
          </SearchInputWrapper>
          <ViewGrid>
            <ViewButton
              active={organizationView === 'categories'}
              onClick={() => setOrganizationView('categories')}
              disabled={isLoading}
            >
              <TagIcon />
              {t(NEW_TEMPLATE_SECTION.CATEGORIES.key, NEW_TEMPLATE_SECTION.CATEGORIES.value)}
            </ViewButton>
            <ViewButton
              active={organizationView === 'folders'}
              onClick={() => setOrganizationView('folders')}
              disabled={isLoading}
            >
              <FolderIconSVG />
              {t(NEW_TEMPLATE_SECTION.FOLDERS.key, NEW_TEMPLATE_SECTION.FOLDERS.value)}
            </ViewButton>
            <ViewButton
              active={organizationView === 'teams'}
              onClick={() => setOrganizationView('teams')}
              disabled={isLoading}
            >
              <UsersIcon />
              {t(NEW_TEMPLATE_SECTION.TEAMS.key, NEW_TEMPLATE_SECTION.TEAMS.value)}
            </ViewButton>
            <ViewButton
              active={organizationView === 'languages'}
              onClick={() => setOrganizationView('languages')}
              disabled={isLoading}
            >
              <GlobeIcon />
              {t(NEW_TEMPLATE_SECTION.LANGUAGES.key, NEW_TEMPLATE_SECTION.LANGUAGES.value)}
            </ViewButton>
          </ViewGrid>
        </ViewSwitcherSection>

        {/* Navigation */}
        <NavigationSection>
          {/* Quick Access */}
          <NavGroup>
            <NavGroupHeader>
              <NavGroupTitle>
                {t(NEW_TEMPLATE_SECTION.QUICK_ACCESS.key, NEW_TEMPLATE_SECTION.QUICK_ACCESS.value)}
              </NavGroupTitle>
            </NavGroupHeader>
            <NavItems>
              <FolderItem
                id="all"
                name={t(NEW_TEMPLATE_SECTION.ALL.key, NEW_TEMPLATE_SECTION.ALL.value)}
                icon={<FileTextIconSVG size={16} />}
                count={templateCount?.communicationCount || templateCount?.templateCount}
                selectedFolder={selectedFolder}
                onSelectFolder={onSelectFolder}
                status="all"
                clearAllFilters={clearAllFilters}
              />
            </NavItems>
          </NavGroup>

          {/* Document Status */}
          <NavGroup>
            <NavGroupHeader>
              <NavGroupTitle>
                {t(
                  NEW_TEMPLATE_SECTION.STATUS.key,
                  NEW_TEMPLATE_SECTION.STATUS.value,
                )}
              </NavGroupTitle>
            </NavGroupHeader>
            <NavItems>
              <FolderItem
                id="waiting"
                name={t(
                  NEW_TEMPLATE_SECTION.WAITING_FOR_APPROVAL.key,
                  NEW_TEMPLATE_SECTION.WAITING_FOR_APPROVAL.value,
                )}
                icon={<ClockIconSVG />}
                count={templateCount?.waitingForApprovalCount}
                selectedFolder={selectedFolder}
                onSelectFolder={onSelectFolder}
                status="waiting"
                qaStatus={qaStatus}
                setQaStatus={setQaStatus}
              />

              <FolderItem
                id="rejected"
                name={t(
                  NEW_TEMPLATE_SECTION.NEEDS_CHANGES.key,
                  NEW_TEMPLATE_SECTION.NEEDS_CHANGES.value,
                )}
                icon={<CircleXIcon />}
                count={templateCount?.rejectedCounts}
                selectedFolder={selectedFolder}
                onSelectFolder={onSelectFolder}
                status="rejected"
                qaStatus={qaStatus}
                setQaStatus={setQaStatus}
              />
            </NavItems>
          </NavGroup>

          {/* Search by Tags */}
          <NavGroup>
            <NavGroupHeader>
              <NavGroupTitle>
                {t(
                  NEW_TEMPLATE_SECTION.SEARCH_BY_TAGS.key,
                  NEW_TEMPLATE_SECTION.SEARCH_BY_TAGS.value,
                )}
              </NavGroupTitle>
            </NavGroupHeader>
            <TagSearch selectedTags={selectedTags} onTagsChange={setSelectedTags} />
          </NavGroup>

          {/* Dynamic Content Based on View */}
          <NavGroup>
            <NavGroupHeader>
              <NavGroupTitle>{titleMap[organizationView]}</NavGroupTitle>
              {/* {(organizationView === 'categories' ||
                organizationView === 'folders' ||
                (organizationView === 'teams' && checkUserAccessRoles(['ADMIN']))) && (
                <Popover
                  trigger="click"
                  content={
                    <CreateResource
                      selectedView={selectedView}
                      organizationView={organizationView}
                    />
                  }
                >
                  <AddButton>
                    <Icon type="plus" size={14} />
                  </AddButton>
                </Popover>
              )} */}
            </NavGroupHeader>
            {isLoading ? (
              <SidebarLoader>
                <Icon type="loading" />
              </SidebarLoader>
            ) : (organizationView === 'languages' ? (
              languages.length > 0
            ) : (
              templateCount?.[keys[organizationView]]?.length > 0
            )) ? (
              <NavItems>
                <RecursiveFolderTree
                  folders={
                    organizationView === 'languages'
                      ? languages
                      : templateCount?.[keys[organizationView]] || []
                  }
                  selectedFolder={selectedFolder}
                  onSelectFolder={onSelectFolder}
                />
              </NavItems>
            ) : (
              <Empty>
                <EmptyIcon>
                  <Icon type="inbox" size={40} />
                </EmptyIcon>
                <p>
                  {t(
                    NEW_TEMPLATE_SECTION.NO_DATA_AVAILABLE.key,
                    NEW_TEMPLATE_SECTION.NO_DATA_AVAILABLE.value,
                  )}
                </p>
              </Empty>
            )}
          </NavGroup>
        </NavigationSection>
      </SidebarContent>
    </SidebarContainer>
  );
};

export default Sidebar;
